"use client"

import Text from "@/app/(site)/_components/Atoms/Text"
import { FOCUS_VISIBLE_CLASS } from "@/src/utils/a11y/focusVisible"
import { clsx } from "@/src/utils/clsx"
import React, { useEffect, useRef } from "react"

export type FilterOption = {
    value: string
    label: string
}

type Props = {
    options: FilterOption[]
    selectedValue: string
    /** Id of the control that opens this panel, or of the panel heading. */
    labelledBy: string
    onSelect: (value: string) => void
    /** Escape: close and hand focus back to the trigger. */
    onDismiss: () => void
    /** Popups take focus when they open; the inline mobile panel does not. */
    autoFocus?: boolean
    /** Popups close when focus tabs past the last option. */
    onFocusLeave?: () => void
}

/**
 * Plain toggle buttons, each its own tab stop. Native radios were checking
 * whatever the arrow keys landed on, and every check pushed a new URL, so the
 * filter applied before the user had chosen. Buttons only act on Enter/Space.
 */
export default function FilterOptionGroup({
    options,
    selectedValue,
    labelledBy,
    onSelect,
    onDismiss,
    autoFocus = false,
    onFocusLeave
}: Props) {
    const containerRef = useRef<HTMLDivElement>(null)
    const itemRefs = useRef<(HTMLButtonElement | null)[]>([])

    useEffect(() => {
        if (!autoFocus) return
        const selectedIndex = options.findIndex(option => option.value === selectedValue)
        itemRefs.current[Math.max(selectedIndex, 0)]?.focus()
        // Mount only. Re-running on selection would pull focus back into the
        // panel after the caller has already returned it to the trigger.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key !== "Escape") return
        event.preventDefault()
        onDismiss()
    }

    const handleBlur = (event: React.FocusEvent<HTMLDivElement>) => {
        if (!onFocusLeave) return
        const nextFocused = event.relatedTarget as Node | null
        // A null target means the page lost focus entirely (window switch) or
        // the click landed on something unfocusable — leave the panel open and
        // let the click-outside handler decide.
        if (!nextFocused || containerRef.current?.contains(nextFocused)) return
        onFocusLeave()
    }

    return (
        <div
            ref={containerRef}
            role="group"
            aria-labelledby={labelledBy}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            className="flex flex-col gap-16"
        >
            {options.map((option, index) => {
                const isSelected = option.value === selectedValue

                return (
                    <button
                        key={option.value}
                        ref={element => {
                            itemRefs.current[index] = element
                        }}
                        type="button"
                        aria-pressed={isSelected}
                        onClick={() => onSelect(option.value)}
                        className={clsx(
                            "flex cursor-pointer items-center text-left",
                            FOCUS_VISIBLE_CLASS
                        )}
                    >
                        <span className="relative flex items-center">
                            <span
                                className={clsx(
                                    "h-24 w-24 rounded-full border-2 border-black",
                                    isSelected && "bg-black"
                                )}
                            />
                            {isSelected && (
                                <span className="absolute inset-0 h-8 w-8 translate-x-8 translate-y-8 bg-main-white" />
                            )}
                        </span>
                        <Text
                            asChild
                            size="medium"
                            weight="regular"
                            className="select-none pl-8 text-black"
                        >
                            <span>{option.label}</span>
                        </Text>
                    </button>
                )
            })}
        </div>
    )
}
